import React from "react";
import { css } from "@emotion/core";
import { isFunction } from "lodash";
import { Fragment, useCallback, useEffect, useState } from "react";
import { SplitFactory, SplitTreatments } from "@splitsoftware/splitio-react";

import {
  useChangePlan,
  useCurrentAccountId,
  usePaymentIntentsMachine,
} from "@zapier/checkout/esm/hooks";
import { breakpoints } from "@zapier/checkout/esm/constants";

import {
  Alert,
  AlertDescription,
  Colors,
  Margin,
  SkeletonBlock,
  Spacer,
  useTheme,
} from "@zapier/design-system";

import {
  CurrencyRulesetItem,
  PaymentApp,
  PendingPaymentWarningBar,
  useCurrencyRuleset,
  usePaymentSource,
  usePendingPayment,
} from "@zapier/payment";

import { CurrencyProvider } from "@zapier/currency";
import { buildDowngradeWarningUrl } from "@zapier/downgrade";

import {
  useMaybeSendFullStoryEvent,
  useTrackingContext,
} from "@zapier/toolbox-tracking";

import {
  withApolloInjectionProvider,
  useApolloInjection,
} from "@zapier/checkout/esm/contexts/ApolloInjection";

import {
  ApolloInjection,
  ChangeType,
  Interval,
  PlanV2,
} from "@zapier/checkout/esm/types";

import { ChangePlanCompleteModal } from "../ChangePlanCompleteModal";
import { Document } from "../Document";

import { AsyncOverlay } from "./AsyncOverlay";
import { BillingOptions } from "./BillingOptions";
import { ChangePlanButtons } from "./ChangePlanButtons";
import { ChangePlanCurrencyDropdown } from "./ChangePlanCurrencyDropdown";
import { ChangePlanHeader } from "./ChangePlanHeader";
import { ChangePlanIndicator } from "./ChangePlanIndicator";
import { ChangePlanSection } from "./ChangePlanSection";
import { ChangePlanSummary } from "./ChangePlanSummary";
import {
  PaymentIntentFatalErrorModal,
  RetryPaymentIntentAlert,
} from "./PaymentIntentErrorUi";

import {
  useCurrentAccount,
  useInvoicePreview,
  usePlans,
  usePromoPlans,
  useSelectedCurrency,
} from "./hooks";

import {
  getChangeLabel,
  getChangeLabelForPlanName,
} from "@zapier/checkout/esm/utils";

const AUTO_UPGRADE_FEATURE_NAME = "AUTO_UPGRADE";

type Props = {
  /** `upgrade`, `downgrade` etc. */
  changeType: ChangeType;
  /** Allows changing of wrapper styles */
  cssOverrides?: { [key: string]: string };
  /** ISO code for selected currency */
  currency?: string;
  /** The interval selected. Should be used when controlled via `onSelectInterval` */
  interval?: Interval;
  /** Hides the header */
  isCompact?: boolean;
  /** Set this to true when the change plan page appears inside an inline upgrade nudge. */
  isEmbeddedInInlineUpgrade?: boolean;
  /** Called when cancel button is clicked */
  onCancel: () => void;
  /** Called when change plan action is successful */
  onFinishChangePlan?: () => void;
  /** Called when interval is clicked. Passing a function here makes this a controlled component */
  onSelectInterval?: (interval: Interval) => void;
  /** Called when currency is selected. Passing a function here makes this a controlled component. Use with currency prop. */
  onSelectCurrency?: (currency: string) => void;
  /** Flag to indicate whether to route to downgrade confirmation page before downgrading */
  showDowngradeConfirmation?: boolean;
  /** Flag to indicate whether to show the change plan success modal or not */
  showSuccessModal: boolean;
  /** The name of the plan changing to */
  toPlanName: string;
  /** Temporary: allows Split.io localhost mode */
  splitLocalhostMode?: boolean;
} & ApolloInjection;

const changePlanCss = (props: Props) =>
  css([
    css`
      background-color: ${Colors.white};
      border: 1px solid #dadfe2;
      border-radius: 5px;
      margin: 2em auto;
      max-width: 800px;
      padding: 2em;
      width: auto;
    `,
    props.isCompact &&
      css`
        border: none;
      `,
    props.cssOverrides && css(props.cssOverrides),
  ]);

const rebrandChangePlanCss = css`
  background-color: ${Colors.neutral100};
  border-color: ${Colors.neutral400};
  @media screen and (max-width: ${breakpoints.mobileMax}px) {
    border: none;
    margin-top: 0;
  }
`;

const buttonsCss = css`
  margin-top: 2em;
`;

const errorCss = css`
  margin-top: 18px;
  text-align: center;
`;

const findPlans = (
  toPlanName: string,
  plans: PlanV2[],
  currentInterval: Interval
) => {
  const altInterval = currentInterval === "year" ? "month" : "year";
  const toPlanNameLower = toPlanName.toLowerCase();

  let toPlan = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() && currentInterval === p.interval
  );

  toPlan =
    toPlan ??
    plans.find(
      p =>
        toPlanNameLower === p.name.toLowerCase() && altInterval === p.interval
    );

  // The alternative interval plan for the selected plan
  const toPlanAlt = plans.find(
    p =>
      toPlanNameLower === p.name.toLowerCase() &&
      p.tasks === toPlan?.tasks &&
      p.zaps === toPlan?.zaps &&
      p.interval !== toPlan.interval
  );

  return [toPlan, toPlanAlt];
};

const ChangePlanComponentWithPaymentIntents = (props: Props) => {
  const {
    toPlanName,
    changeType,
    currency,
    interval,
    isEmbeddedInInlineUpgrade = false,
    onSelectCurrency,
    onSelectInterval,
    showSuccessModal,
  } = props;

  const { isRebrand } = useTheme();
  const { useQuery, useMutation } = useApolloInjection();
  const { trackEvent } = useTrackingContext();
  const { accountId } = useCurrentAccountId({ useQuery });

  const { currentAccount, isLoading: isLoadingCurrentAccount } =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useCurrentAccount(accountId!, { skip: !accountId }, useQuery);

  const autoUpgradeEnrollment = (
    currentAccount?.featureEnrollmentList || []
  ).find(e => e?.featureName === AUTO_UPGRADE_FEATURE_NAME);

  const {
    currentPlan,
    plans,
    isLoading: isLoadingPlans,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  } = usePlans(accountId!, { skip: !accountId }, useQuery);

  const { isLoading: isLoadingPromoPlans, promoPlans } =
    usePromoPlans(useQuery);

  // INTERVAL STATE -------------
  const [selectedInterval, setSelectedInterval] = useState<Interval>("year");
  // Current interval is props.interval if being controlled, or internal state if uncontrolled
  const currentInterval = interval ?? selectedInterval;

  // CURRENCY STATE -------------
  const {
    selectedCurrency,
    setSelectedCurrency,
    isLoading: isLoadingSelectedCurrency,
  } = useSelectedCurrency({
    currency,
    onSelectCurrency,
    useQuery,
  });

  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId,
    useQuery,
  });

  const { pendingInvoice } = usePendingPayment({ useQuery });

  const { currencyRuleset, isLoading: isLoadingCurrencyRuleset } =
    useCurrencyRuleset({ useQuery });

  const isLoading =
    isLoadingCurrentAccount ||
    isLoadingPlans ||
    isLoadingPromoPlans ||
    isLoadingSelectedCurrency ||
    isLoadingCurrencyRuleset;

  const plansWithPromos = isLoading ? [] : [...plans, ...promoPlans];

  useMaybeSendFullStoryEvent(
    currentAccount?.switches?.trackUpgradesByPlan && changeType === "upgrade",
    `Began Upgrade From Plan ${currentPlan?.id}`
  );

  const [toPlan, toPlanAlt] = findPlans(
    toPlanName,
    plansWithPromos as PlanV2[],
    currentInterval
  );

  // INVOICE PREVIEW -------------
  const {
    error: invoiceError,
    invoice,
    isLoading: isLoadingInvoice,
  } = useInvoicePreview({
    accountId: currentAccount?.id as number,
    planId: toPlan?.id,
    currency: selectedCurrency.name,
    paymentSourceId: primarySource?.id,
    useQuery,
  });

  // CHANGE PLAN -------------
  const {
    changePlan,
    changePlanError,
    hasChangedPlan,
    isChangingPlan,
    pausedZaps,
  } = useChangePlan({
    accountId,
    planId: toPlan?.id,
    downgradeReason: null,
    currency: selectedCurrency.name,
    invoicedAt: invoice?.invoicedAt,
    paymentIntentId: invoice?.paymentIntent?.id,
    useQuery,
    useMutation,
  });

  const shouldShowDowngradeConfirmation =
    changeType === "downgrade" && props.showDowngradeConfirmation;

  const onChangePlan = async () => {
    if (shouldShowDowngradeConfirmation) {
      return null;
    }

    await changePlan();

    trackEvent("Billing: plan changed", {
      accountId,
      plan: toPlan?.name,
      planId: toPlan?.id,
      interval,
    });

    if (isFunction(props.onFinishChangePlan)) {
      return props.onFinishChangePlan();
    }
    return null;
  };

  const onCancelChange = () => {
    trackEvent("Billing: cancelChangePlan", {
      accountId,
      fromPlanName: currentPlan?.name,
      toPlanName: toPlan?.name,
      interval,
      changeType,
      step: "first step",
    });

    props.onCancel();
  };

  const onSelectIntervalCallback = useCallback(
    (newInterval: Interval) => {
      return isFunction(onSelectInterval)
        ? onSelectInterval(newInterval)
        : setSelectedInterval(newInterval);
    },
    [onSelectInterval]
  );

  // Ensure selected interval and `toPlan.interval` match
  useEffect(() => {
    if (
      !isLoading &&
      plansWithPromos.length > 0 &&
      toPlan &&
      toPlan.interval !== currentInterval
    ) {
      onSelectIntervalCallback(toPlan.interval);
    }
  }, [
    currentInterval,
    isLoading,
    onSelectIntervalCallback,
    plansWithPromos,
    toPlan,
  ]);

  const changeLabel = getChangeLabel(changeType);
  const heading = getChangeLabelForPlanName(
    changeType,
    toPlan?.name ?? "",
    isRebrand
  );
  const subheading = `${changeLabel} email`;

  const submitText =
    changeType === "change"
      ? `${changeLabel} Account Billing`
      : `${changeLabel} My Account`;

  const rebrandSubmitText =
    changeType === "change"
      ? `${changeLabel} account billing`
      : `${changeLabel} my account`;

  const hasNoPaymentSource =
    toPlan && toPlan.amount !== 0 && (!primarySource || forceSourceEdit);

  const rulesetFilteredByCurrency = currencyRuleset.filter(
    (rule: CurrencyRulesetItem) => rule.currency === selectedCurrency.name
  );

  const supportedPaymentMethods = rulesetFilteredByCurrency.map(
    (rule: CurrencyRulesetItem) => rule.method
  );
  const supportedPaymentSubmethods = rulesetFilteredByCurrency.map(
    (rule: CurrencyRulesetItem) => rule.submethod
  );

  const hasUnsupportedPaymentSource =
    (primarySource?.method &&
      !supportedPaymentMethods.includes(primarySource.method)) ||
    (primarySource?.submethod &&
      !supportedPaymentSubmethods.includes(primarySource.submethod));

  /**
   *  A user on the same plan and interval shouldn't be allowed to proceed since it's essentially a no-op
   */
  const isSamePlanAndInterval =
    currentPlan?.id === toPlan?.id &&
    currentPlan?.interval === toPlan?.interval;

  const isSubmitDisabled =
    isLoadingInvoice ||
    hasNoPaymentSource ||
    Boolean(pendingInvoice) ||
    hasUnsupportedPaymentSource ||
    isSamePlanAndInterval;

  const popoverText = hasNoPaymentSource
    ? "Please add a payment method."
    : pendingInvoice
    ? "Cannot change plan with a pending payment."
    : hasUnsupportedPaymentSource
    ? "Please use a supported payment method."
    : null;

  const {
    isVerifyingPayment,
    hasFatalError,
    hasPaymentConfirmationError,
    submit,
    retryPayment,
  } = usePaymentIntentsMachine(invoice?.paymentIntent, onChangePlan);

  return (
    <CurrencyProvider selectedCurrency={selectedCurrency}>
      <Document title={heading}>
        {isLoading ? (
          /**
           * 600x750 makes loading the change plan page in a modal look smooth. If we want a different
           * loading block for non-modal usage, we may want to add an `isInModal` prop or something
           * to the `ChangePlan` component and conditionally render an appropriate loading block.
           */
          <SkeletonBlock height={600} width={750} />
        ) : (
          <Fragment>
            {showSuccessModal && hasChangedPlan && (
              <ChangePlanCompleteModal
                changeType={changeType}
                hasPausedZaps={pausedZaps.length > 0}
                newPlan={toPlan as PlanV2}
              />
            )}
            <AsyncOverlay
              isVisible={isVerifyingPayment || isChangingPlan}
              renderAsyncIndicator={() => <ChangePlanIndicator />}
            >
              <section
                css={[changePlanCss(props), isRebrand && rebrandChangePlanCss]}
              >
                {!props.isCompact && (
                  <ChangePlanHeader
                    accountName={
                      currentAccount?.owner?.email ?? currentAccount?.name
                    }
                    companyName={currentAccount?.companyName}
                    heading={heading}
                    isEmbeddedInInlineUpgrade={isEmbeddedInInlineUpgrade}
                    subheading={subheading}
                  />
                )}

                <ChangePlanCurrencyDropdown
                  onSelectCurrency={setSelectedCurrency}
                  selectedCurrency={selectedCurrency}
                />

                <Spacer height={20} />

                {pendingInvoice && (
                  <Margin margin="0 0 10px 0">
                    <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
                  </Margin>
                )}

                <BillingOptions
                  isAutoUpgradeEnrolled={autoUpgradeEnrollment?.isEnrolled}
                  onSelectOption={onSelectIntervalCallback}
                  planOptions={([toPlan, toPlanAlt] as PlanV2[])
                    .sort((a, b) => b.amount - a.amount)
                    .filter(p => p !== undefined)}
                  selectedOption={currentInterval}
                />

                {toPlan && toPlan.amount !== 0 && (
                  <ChangePlanSection
                    {...(!hasNoPaymentSource
                      ? { title: "Payment information" }
                      : {})}
                  >
                    <PaymentApp
                      editInModal={true}
                      selectedCurrency={selectedCurrency}
                      useMutation={useMutation}
                      useQuery={useQuery}
                    />
                  </ChangePlanSection>
                )}

                <ChangePlanSection title="Summary">
                  {toPlan && (
                    <ChangePlanSummary
                      error={invoiceError}
                      invoice={invoice}
                      isLoading={isLoadingInvoice}
                      plan={toPlan}
                    />
                  )}
                </ChangePlanSection>

                <div css={buttonsCss}>
                  <ChangePlanButtons
                    downgradeHref={
                      shouldShowDowngradeConfirmation
                        ? buildDowngradeWarningUrl(
                            currentPlan?.name ?? "",
                            toPlanName,
                            currentInterval,
                            selectedCurrency.name
                          )
                        : undefined
                    }
                    isDisabled={isSubmitDisabled}
                    onCancelChangePlan={onCancelChange}
                    onChangePlan={submit}
                    popoverText={popoverText}
                    submitText={isRebrand ? rebrandSubmitText : submitText}
                  />
                </div>

                {changePlanError && (
                  <div css={errorCss}>
                    <Alert type="error">
                      <AlertDescription>{changePlanError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                {hasFatalError ? (
                  <PaymentIntentFatalErrorModal />
                ) : hasPaymentConfirmationError ? (
                  <RetryPaymentIntentAlert retry={retryPayment} />
                ) : null}
              </section>
            </AsyncOverlay>
          </Fragment>
        )}
      </Document>
    </CurrencyProvider>
  );
};
export const ChangePlanComponent = (props: Props) => {
  const {
    toPlanName,
    changeType,
    currency,
    interval,
    isEmbeddedInInlineUpgrade = false,
    onSelectCurrency,
    onSelectInterval,
    showSuccessModal,
  } = props;

  const { isRebrand } = useTheme();
  const { useQuery, useMutation } = useApolloInjection();
  const { trackEvent } = useTrackingContext();
  const { accountId } = useCurrentAccountId({ useQuery });

  const { currentAccount, isLoading: isLoadingCurrentAccount } =
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useCurrentAccount(accountId!, { skip: !accountId }, useQuery);

  const autoUpgradeEnrollment = (
    currentAccount?.featureEnrollmentList || []
  ).find(e => e?.featureName === AUTO_UPGRADE_FEATURE_NAME);

  const {
    currentPlan,
    plans,
    isLoading: isLoadingPlans,
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  } = usePlans(accountId!, { skip: !accountId }, useQuery);

  const { isLoading: isLoadingPromoPlans, promoPlans } =
    usePromoPlans(useQuery);

  // INTERVAL STATE -------------
  const [selectedInterval, setSelectedInterval] = useState<Interval>("year");
  // Current interval is props.interval if being controlled, or internal state if uncontrolled
  const currentInterval = interval ?? selectedInterval;

  // CURRENCY STATE -------------
  const {
    selectedCurrency,
    setSelectedCurrency,
    isLoading: isLoadingSelectedCurrency,
  } = useSelectedCurrency({
    currency,
    onSelectCurrency,
    useQuery,
  });

  const { forceSourceEdit, primarySource } = usePaymentSource({
    accountId,
    useQuery,
  });

  const { pendingInvoice } = usePendingPayment({ useQuery });

  const { currencyRuleset, isLoading: isLoadingCurrencyRuleset } =
    useCurrencyRuleset({ useQuery });

  const isLoading =
    isLoadingCurrentAccount ||
    isLoadingPlans ||
    isLoadingPromoPlans ||
    isLoadingSelectedCurrency ||
    isLoadingCurrencyRuleset;

  const plansWithPromos = isLoading ? [] : [...plans, ...promoPlans];

  useMaybeSendFullStoryEvent(
    currentAccount?.switches?.trackUpgradesByPlan && changeType === "upgrade",
    `Began Upgrade From Plan ${currentPlan?.id}`
  );

  const [toPlan, toPlanAlt] = findPlans(
    toPlanName,
    plansWithPromos as PlanV2[],
    currentInterval
  );

  // INVOICE PREVIEW -------------
  const {
    error: invoiceError,
    invoice,
    isLoading: isLoadingInvoice,
  } = useInvoicePreview({
    accountId: currentAccount?.id as number,
    planId: toPlan?.id,
    currency: selectedCurrency.name,
    paymentSourceId: primarySource?.id,
    useQuery,
  });

  // CHANGE PLAN -------------
  const {
    changePlan,
    changePlanError,
    hasChangedPlan,
    isChangingPlan,
    pausedZaps,
  } = useChangePlan({
    accountId,
    planId: toPlan?.id,
    downgradeReason: null,
    currency: selectedCurrency.name,
    invoicedAt: invoice?.invoicedAt,
    useQuery,
    useMutation,
  });

  const shouldShowDowngradeConfirmation =
    changeType === "downgrade" && props.showDowngradeConfirmation;

  const onChangePlan = async () => {
    if (shouldShowDowngradeConfirmation) {
      return null;
    }

    await changePlan();

    trackEvent("Billing: plan changed", {
      accountId,
      plan: toPlan?.name,
      planId: toPlan?.id,
      interval,
    });

    if (isFunction(props.onFinishChangePlan)) {
      return props.onFinishChangePlan();
    }
    return null;
  };

  const onCancelChange = () => {
    trackEvent("Billing: cancelChangePlan", {
      accountId,
      fromPlanName: currentPlan?.name,
      toPlanName: toPlan?.name,
      interval,
      changeType,
      step: "first step",
    });

    props.onCancel();
  };

  const onSelectIntervalCallback = useCallback(
    (newInterval: Interval) => {
      return isFunction(onSelectInterval)
        ? onSelectInterval(newInterval)
        : setSelectedInterval(newInterval);
    },
    [onSelectInterval]
  );

  // Ensure selected interval and `toPlan.interval` match
  useEffect(() => {
    if (
      !isLoading &&
      plansWithPromos.length > 0 &&
      toPlan &&
      toPlan.interval !== currentInterval
    ) {
      onSelectIntervalCallback(toPlan.interval);
    }
  }, [
    currentInterval,
    isLoading,
    onSelectIntervalCallback,
    plansWithPromos,
    toPlan,
  ]);

  const changeLabel = getChangeLabel(changeType);
  const heading = getChangeLabelForPlanName(
    changeType,
    toPlan?.name ?? "",
    isRebrand
  );
  const subheading = `${changeLabel} email`;

  const submitText =
    changeType === "change"
      ? `${changeLabel} Account Billing`
      : `${changeLabel} My Account`;

  const rebrandSubmitText =
    changeType === "change"
      ? `${changeLabel} account billing`
      : `${changeLabel} my account`;

  const hasNoPaymentSource =
    toPlan && toPlan.amount !== 0 && (!primarySource || forceSourceEdit);

  const rulesetFilteredByCurrency = currencyRuleset.filter(
    (rule: CurrencyRulesetItem) => rule.currency === selectedCurrency.name
  );

  const supportedPaymentMethods = rulesetFilteredByCurrency.map(
    (rule: CurrencyRulesetItem) => rule.method
  );
  const supportedPaymentSubmethods = rulesetFilteredByCurrency.map(
    (rule: CurrencyRulesetItem) => rule.submethod
  );

  const hasUnsupportedPaymentSource =
    (primarySource?.method &&
      !supportedPaymentMethods.includes(primarySource.method)) ||
    (primarySource?.submethod &&
      !supportedPaymentSubmethods.includes(primarySource.submethod));

  /**
   *  A user on the same plan and interval shouldn't be allowed to proceed since it's essentially a no-op
   */
  const isSamePlanAndInterval =
    currentPlan?.id === toPlan?.id &&
    currentPlan?.interval === toPlan?.interval;

  const isSubmitDisabled =
    isLoadingInvoice ||
    hasNoPaymentSource ||
    Boolean(pendingInvoice) ||
    hasUnsupportedPaymentSource ||
    isSamePlanAndInterval;

  const popoverText = hasNoPaymentSource
    ? "Please add a payment method."
    : pendingInvoice
    ? "Cannot change plan with a pending payment."
    : hasUnsupportedPaymentSource
    ? "Please use a supported payment method."
    : null;

  return (
    <CurrencyProvider selectedCurrency={selectedCurrency}>
      <Document title={heading}>
        {isLoading ? (
          /**
           * 600x750 makes loading the change plan page in a modal look smooth. If we want a different
           * loading block for non-modal usage, we may want to add an `isInModal` prop or something
           * to the `ChangePlan` component and conditionally render an appropriate loading block.
           */
          <SkeletonBlock height={600} width={750} />
        ) : (
          <Fragment>
            {showSuccessModal && hasChangedPlan && (
              <ChangePlanCompleteModal
                changeType={changeType}
                hasPausedZaps={pausedZaps.length > 0}
                newPlan={toPlan as PlanV2}
              />
            )}
            <AsyncOverlay
              isVisible={isChangingPlan}
              renderAsyncIndicator={() => <ChangePlanIndicator />}
            >
              <section
                css={[changePlanCss(props), isRebrand && rebrandChangePlanCss]}
              >
                {!props.isCompact && (
                  <ChangePlanHeader
                    accountName={
                      currentAccount?.owner?.email ?? currentAccount?.name
                    }
                    companyName={currentAccount?.companyName}
                    heading={heading}
                    isEmbeddedInInlineUpgrade={isEmbeddedInInlineUpgrade}
                    subheading={subheading}
                  />
                )}

                <ChangePlanCurrencyDropdown
                  onSelectCurrency={setSelectedCurrency}
                  selectedCurrency={selectedCurrency}
                />

                <Spacer height={20} />

                {pendingInvoice && (
                  <Margin margin="0 0 10px 0">
                    <PendingPaymentWarningBar pendingInvoice={pendingInvoice} />
                  </Margin>
                )}

                <BillingOptions
                  isAutoUpgradeEnrolled={autoUpgradeEnrollment?.isEnrolled}
                  onSelectOption={onSelectIntervalCallback}
                  planOptions={([toPlan, toPlanAlt] as PlanV2[])
                    .sort((a, b) => b.amount - a.amount)
                    .filter(p => p !== undefined)}
                  selectedOption={currentInterval}
                />

                {toPlan && toPlan.amount !== 0 && (
                  <ChangePlanSection
                    {...(!hasNoPaymentSource
                      ? { title: "Payment information" }
                      : {})}
                  >
                    <PaymentApp
                      editInModal={true}
                      selectedCurrency={selectedCurrency}
                      useMutation={useMutation}
                      useQuery={useQuery}
                    />
                  </ChangePlanSection>
                )}

                <ChangePlanSection title="Summary">
                  {toPlan && (
                    <ChangePlanSummary
                      error={invoiceError}
                      invoice={invoice}
                      isLoading={isLoadingInvoice}
                      plan={toPlan}
                    />
                  )}
                </ChangePlanSection>

                <div css={buttonsCss}>
                  <ChangePlanButtons
                    downgradeHref={
                      shouldShowDowngradeConfirmation
                        ? buildDowngradeWarningUrl(
                            currentPlan?.name ?? "",
                            toPlanName,
                            currentInterval,
                            selectedCurrency.name
                          )
                        : undefined
                    }
                    isDisabled={isSubmitDisabled}
                    onCancelChangePlan={onCancelChange}
                    onChangePlan={onChangePlan}
                    popoverText={popoverText}
                    submitText={isRebrand ? rebrandSubmitText : submitText}
                  />
                </div>

                {changePlanError && (
                  <div css={errorCss}>
                    <Alert type="error">
                      <AlertDescription>{changePlanError}</AlertDescription>
                    </Alert>
                  </div>
                )}
              </section>
            </AsyncOverlay>
          </Fragment>
        )}
      </Document>
    </CurrencyProvider>
  );
};

const SplitChangePlan = (props: Props) => {
  const { useQuery } = useApolloInjection();
  const { accountId } = useCurrentAccountId({ useQuery });
  const { currentAccount } = useCurrentAccount(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    accountId!,
    { skip: !accountId },
    useQuery
  );

  // These are temporary and will be removed after full rollout out SetupIntents/PaymentIntents
  const isProduction =
    typeof window !== "undefined" &&
    window.location.hostname.includes("zapier.com");

  const authorizationKey = isProduction
    ? "mctu4q1tvfa8hgn8kgt5doe0ev6h68th135"
    : "tra7b4qvk62o373ivf9gc24547fp5cqdv6u0";

  if (!accountId) return null;

  return (
    <SplitFactory
      config={{
        core: {
          authorizationKey: props.splitLocalhostMode
            ? "localhost"
            : authorizationKey,
          key: String(accountId),
        },
        ...((props.splitLocalhostMode ?? false) && {
          features: { billing_fe_payment_intents: "on" },
        }),
      }}
    >
      <SplitTreatments
        attributes={{ email: currentAccount?.owner?.email }}
        names={["billing_fe_payment_intents"]}
      >
        {({ treatments, isReady }) => {
          return isReady &&
            treatments.billing_fe_payment_intents.treatment === "on" ? (
            <ChangePlanComponentWithPaymentIntents {...props} />
          ) : (
            <ChangePlanComponent {...props} />
          );
        }}
      </SplitTreatments>
    </SplitFactory>
  );
};

export const ChangePlan = withApolloInjectionProvider<Props>(SplitChangePlan);
